<template lang="html">
  <div class="cart-section flex min-w-0 flex-col gap-2">
    <div v-if="inCart" class="flex min-w-0 gap-2">
      <div
        class="flex min-w-0 flex-1 items-center justify-between gap-2 border border-default dark:border-gray-700"
      >
        <UButton
          icon="i-lucide-minus"
          color="neutral"
          variant="ghost"
          :disabled="isLoading"
          @click="handleDecrease"
        />

        <AppLoader v-if="checkIsLoading('add') || checkIsLoading('remove')" />
        <div v-else class="font-medium tabular-nums">
          {{ amount }}
        </div>

        <UButton
          icon="i-lucide-plus"
          color="neutral"
          variant="ghost"
          :disabled="isLoading"
          @click="handleIncrease"
        />
      </div>

      <UButton
        icon="i-lucide-trash"
        color="error"
        variant="ghost"
        class="shrink-0"
        :disabled="isLoading"
        @click="handleRemoveFromCart"
      >
        <template #leading>
          <AppLoader v-if="checkIsLoading('remove-all')" />
        </template>
      </UButton>
    </div>

    <UButton
      v-else
      label="В корзину"
      class="justify-center self-stretch"
      :disabled="isLoading"
      @click="handleAddToCart"
    >
      <template #leading>
        <AppLoader v-if="checkIsLoading('add')" />
      </template>
    </UButton>
  </div>
</template>

<script setup lang="ts">
import type { ProductDto } from '@repo/api/web/models'

import { useEcommerce } from '#Analytics/composables/useEcommerce'
import { mapCatalogProduct } from '#Analytics/utils/ecommerceMapper'
import { useCart } from '#Cart/composables/useCart'
import { useRequest } from '#Common/composables/useRequest'
import AppLoader from '~/app/components/Loader/AppLoader.vue'

const props = withDefaults(
  defineProps<{
    value: ProductDto
  }>(),
  {},
)

const { checkInCart, addToCart, removeFromCart, getItem } = useCart()
const { send, isLoading, checkIsLoading } = useRequest()
const ecommerce = useEcommerce()

const inCart = computed(() => checkInCart(props.value.id))
const cartItem = computed(() => getItem(props.value.id))
const amount = computed(() => cartItem.value?.amount || 0)

async function handleAddToCart() {
  if (!props.value.id) return

  await send(() => addToCart(props.value.id!), {
    key: 'add',
    successMessage: 'Товар добавлен в корзину',
  })
  ecommerce.add(mapCatalogProduct(props.value, { quantity: 1 }))
}

async function handleIncrease() {
  if (!props.value.id) return

  await send(() => addToCart(props.value.id!, 1), {
    key: 'add',
  })
  ecommerce.add(mapCatalogProduct(props.value, { quantity: 1 }))
}

async function handleDecrease() {
  if (!props.value.id) return

  await send(() => removeFromCart(props.value.id!, 1), {
    key: 'remove',
  })
  ecommerce.remove(mapCatalogProduct(props.value, { quantity: 1 }))
}

async function handleRemoveFromCart() {
  if (!props.value.id) return

  const removedQuantity = amount.value
  await send(() => removeFromCart(props.value.id!), {
    key: 'remove-all',
    successMessage: 'Товар удален из корзины',
  })
  ecommerce.remove(mapCatalogProduct(props.value, { quantity: removedQuantity }))
}
</script>
